import { useState } from "react";
import api from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Landmark, Check, Loader2, PiggyBank } from "lucide-react";
import { toast } from "sonner";

const NEW = "__new__";

export function StatementBanner({ doc, bankAccounts = [], savingsAccounts = [], onApplied }) {
  const st = doc.statement || {};
  const detectedBalance = st.balance != null && st.balance !== "" ? Number(st.balance) : null;
  const detectedName = (st.account_name || "").trim();
  const fallbackName = detectedName || doc.fileName.replace(/\.[^.]+$/, "");

  const [open, setOpen] = useState(detectedBalance != null);
  const [kind, setKind] = useState("current"); // current | savings
  const [balance, setBalance] = useState(detectedBalance != null ? String(detectedBalance) : "");
  const [name, setName] = useState(fallbackName);
  const [busy, setBusy] = useState(false);

  const list = kind === "current" ? bankAccounts : savingsAccounts;
  const matched = detectedName
    ? list.find((a) => (a.name || "").trim().toLowerCase() === detectedName.toLowerCase())
    : null;
  const [target, setTarget] = useState(matched ? matched.id : NEW);

  const changeKind = (v) => {
    setKind(v);
    const nl = v === "current" ? bankAccounts : savingsAccounts;
    const m = detectedName ? nl.find((a) => (a.name || "").trim().toLowerCase() === detectedName.toLowerCase()) : null;
    setTarget(m ? m.id : NEW);
  };

  const nameExists = target === NEW && name.trim() &&
    list.some((a) => (a.name || "").trim().toLowerCase() === name.trim().toLowerCase());

  const wrap = "flex flex-wrap items-center gap-3 px-4 py-3 border-b border-border bg-secondary/40";

  if (doc.statementApplied) {
    return (
      <div className={wrap} data-testid="statement-balance-banner">
        <Landmark className="w-4 h-4 text-primary shrink-0" />
        <span className="text-sm flex-1 min-w-0">Solde enregistré{balance ? <> : <span className="font-num font-semibold">{fmtEuro(Number(balance) || 0)}</span></> : ""}</span>
        <span className="text-xs text-emerald-600 flex items-center gap-1"><Check className="w-3.5 h-3.5" />Compte à jour</span>
      </div>
    );
  }

  const save = async () => {
    const bal = Number(balance);
    if (balance === "" || Number.isNaN(bal)) { toast.error("Saisissez un solde"); return; }
    if (target === NEW && !name.trim()) { toast.error("Nom du compte requis"); return; }
    setBusy(true);
    try {
      if (kind === "current") {
        if (target === NEW) {
          await api.post("/bank-accounts/apply-statement", { name: name.trim(), bank: "", balance: bal });
          toast.success(nameExists ? `Solde de « ${name.trim()} » mis à jour` : `Compte courant « ${name.trim()} » créé`);
        } else {
          const acc = bankAccounts.find((a) => a.id === target);
          await api.put(`/bank-accounts/${target}`, { name: acc.name, bank: acc.bank || "", balance: bal });
          toast.success(`Solde de « ${acc.name} » mis à jour`);
        }
      } else {
        if (target === NEW) {
          const existing = savingsAccounts.find((a) => (a.name || "").trim().toLowerCase() === name.trim().toLowerCase());
          if (existing) {
            await api.put(`/savings/accounts/${existing.id}`, {
              name: existing.name, type: existing.type || "cash", balance: bal,
              institution: existing.institution || "", color: existing.color || "#22293A",
            });
            toast.success(`Solde de « ${existing.name} » (épargne) mis à jour`);
          } else {
            await api.post("/savings/accounts", { name: name.trim(), type: "cash", balance: bal, institution: "", color: "#22293A" });
            toast.success(`Compte épargne « ${name.trim()} » créé`);
          }
        } else {
          const acc = savingsAccounts.find((a) => a.id === target);
          await api.put(`/savings/accounts/${target}`, {
            name: acc.name, type: acc.type || "cash", balance: bal,
            institution: acc.institution || "", color: acc.color || "#22293A",
          });
          toast.success(`Solde de « ${acc.name} » (épargne) mis à jour`);
        }
      }
      onApplied(doc.id);
    } catch { toast.error("Erreur lors de l'enregistrement"); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div className={wrap} data-testid="statement-balance-banner">
        <Landmark className="w-4 h-4 text-primary shrink-0" />
        <span className="text-sm flex-1 min-w-0 text-muted-foreground">Aucun solde de compte détecté automatiquement.</span>
        <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="add-account-manual-button">
          <Landmark className="w-4 h-4 mr-1.5" />Enregistrer le solde d'un compte
        </Button>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 border-b border-border bg-secondary/40 space-y-3" data-testid="statement-balance-banner">
      <div className="flex items-center gap-2 text-sm">
        <Landmark className="w-4 h-4 text-primary shrink-0" />
        {detectedBalance != null
          ? <span>Solde détecté{detectedName ? ` sur « ${detectedName} »` : ""} : <span className="font-num font-semibold">{fmtEuro(detectedBalance)}</span></span>
          : <span className="text-muted-foreground">Aucun solde détecté — saisissez-le pour créer ou mettre à jour un compte.</span>}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Type de compte</Label>
          <Select value={kind} onValueChange={changeKind}>
            <SelectTrigger className="h-9 w-44 text-sm" data-testid="statement-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="current"><span className="flex items-center gap-2"><Landmark className="w-3.5 h-3.5" />Compte courant</span></SelectItem>
              <SelectItem value="savings"><span className="flex items-center gap-2"><PiggyBank className="w-3.5 h-3.5" />Compte épargne</span></SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Compte</Label>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger className="h-9 w-56 text-sm" data-testid="statement-target-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NEW}>➕ Nouveau compte {kind === "savings" ? "épargne" : "courant"}</SelectItem>
              {list.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.institution ? ` · ${a.institution}` : (a.bank ? ` · ${a.bank}` : "")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {target === NEW && (
          <div className="space-y-1">
            <Label className="text-xs">Nom du compte</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 w-56 text-sm" placeholder="Ex: Compte à vue commun" data-testid="statement-account-name-input" />
            {nameExists && <p className="text-[11px] text-amber-700">Ce nom existe déjà — le solde du compte existant sera mis à jour.</p>}
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs">Solde (€)</Label>
          <Input type="number" step="0.01" value={balance} onChange={(e) => setBalance(e.target.value)} className="h-9 w-36 text-sm" placeholder="0,00" data-testid="statement-balance-input" />
        </div>
        <Button size="sm" className="h-9" onClick={save} disabled={busy} data-testid="statement-save-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Check className="w-4 h-4 mr-1.5" />}
          {target === NEW ? "Créer le compte" : "Mettre à jour"}
        </Button>
      </div>
    </div>
  );
}
